import { useCallback, useState } from "react"
import { useParams } from "react-router-dom"
import { useDropzone } from "react-dropzone"
import { BackButton, OrangeButton } from "../Buttons"
import { Error_Component } from "../misc"
import { UploadScreen } from "./UI/UploadScreen"
import { UploadingFile } from "./UI/UploadingFile"
import { uploadFile } from "./api"
import { Alert } from "../_Shared/Form/Buttons/Alert"

const SUPPORTED_FILE_EXTENSIONS = [
    "pdf", // We'll only support PDFs for the moment
]

export function UploadDocumentFile() {
    const { id } = useParams()
    if (!id) {
        return <div>No project ID</div>
    }

    return (
        <UploadFileForm projectId={id} location="documents" />
    )
}

export function UploadProposalFile() {
    const { id } = useParams()
    if (!id) {
        return <div>No project ID</div>
    }

    return (
        <UploadFileForm projectId={id} location="proposals" />
    )
}


// I think thie method of passing in another component with the id is better since I don't break the rules of hooks
// If i decided to add them (this applies to any component that uses useParams)

function UploadFileForm({ projectId, location }: { projectId: string, location: string }) {
    const [file, setFile] = useState<File | null>(null) // This is the current file being uploaded
    const [totalFiles, setTotalFiles] = useState<File[]>([]) // This is the total files after they've been uploaded
    const [error, setError] = useState<string | null>(null)
    const [uploading, setUploading] = useState<boolean>(false)
    const [uploadSuccess, setUploadSuccess] = useState<boolean>(false)

    const locationName = location.charAt(0).toUpperCase() + location.slice(1)

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setError(null)
        
        acceptedFiles.forEach((file) => {
            const maxSize = 100 * 1024 * 1024 // 100MB in bytes
            if (file.size > maxSize) {
                console.warn(`File ${file.name} is too large. Max size is 100MB.`)
                setError(`File ${file.name} is too large. Max size is 100MB.`)
                return 
            }

            const fileExtension = file.name.split('.').pop()
            if (!fileExtension || !SUPPORTED_FILE_EXTENSIONS.includes(fileExtension)) {
                setError(`File ${file.name} is not supported. Only ${SUPPORTED_FILE_EXTENSIONS.join(', ')} files are allowed.`)
                return
            }

            // Check if the file already exists in the current totalFiles array
            // This now correctly works with removed files
            const fileAlreadyExists = totalFiles.some(f => f.name === file.name);
            if (fileAlreadyExists) {
                setError(`File ${file.name} already exists.`)
                return
            }

            setFile(file)
            setTotalFiles([...totalFiles, file])
          })
    }, [totalFiles]) // Add totalFiles to dependency array

    const onRemove = (file: File) => {
        setTotalFiles(totalFiles.filter(f => f.name !== file.name))
    }

    const onUpload = async () => {
        if (totalFiles.length === 0) {
            setError("No file selected")
            return
        }

        setUploading(true)

        try {
            const response = await uploadFile(totalFiles, projectId, location)

            if (response.status === 200) {
                setFile(null)
                setError(null)
                setUploadSuccess(true)
            } else {
                setError("Failed to upload file")
            }
        } catch (error) {
            console.error("Error uploading file:", error)
            setError("Failed to upload file")
        } finally {
            setUploading(false)
        }
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })

    return (
        <div className="flex flex-col gap-4 p-4 h-[60rem]">
            {error && <Error_Component errorString={error} />}
            {uploadSuccess && <Alert
                title="File uploaded successfully"
                description="The file has been uploaded successfully"
                redirects={[
                    {
                        label: "Back to projects",
                        value: "/projects",
                    },
                    {
                        label: "Upload another file",
                        value: "/projects/upload_file/" + projectId,
                    }
                ]}
            />}

            <h1>Upload {locationName} ({projectId})</h1>

            <div className="flex flex-col items-center justify-center h-full p-4 text-center border-2 border-gray-300 border-dashed rounded-md" {...getRootProps()}>
                {uploading ? 
                    <UploadingFile /> 
                    : 
                    <UploadScreen files={totalFiles} isDragActive={isDragActive} getInputProps={getInputProps} setTotalFiles={setTotalFiles} onRemove={onRemove} />
                }
            </div>

            <div className="grid grid-cols-2 gap-4">
                <BackButton />
                <OrangeButton className={`${!file && 'bg-gray-300 hover:bg-gray-300 cursor-not-allowed'}`} onClick={onUpload} disabled={!file} testidName="upload-button">{uploading ? "Uploading..." : "Upload"}</OrangeButton>
            </div>
        </div>
    )
}
